/**
 * @fileoverview パスワードリセット新パスワード入力画面
 *
 * このファイルでは、パスワードリセットメールのリンクをクリックした後に
 * ユーザーが新しいパスワードを設定するための画面を定義しています。
 */

/**
 * 新パスワード入力画面コンポーネント
 *
 * リセットトークンの検証後、ユーザーが新しいパスワードとその確認を
 * 入力するためのフォームを提供します。パスワードの強度検証や
 * 一致確認などの機能を含み、APIと連携してパスワードの更新を行います。
 *
 * @returns {JSX.Element} 新パスワード設定フォーム
 */

import { useState } from "react";
import { Stack, PasswordInput, Text } from "@mantine/core";
import { matchesField, useForm } from "@mantine/form";

import { useLocation } from "react-router";
import { useNavigate } from "@/router";
import { Card } from "@/components/card";
import { Button } from "@/components/button";
import { resetPassword } from "@/api/resetPassword";

export default function ResetPasswordNewPage() {
  const passwordValidator = (value: string) => {
    if (/\s/.test(value)) {
      return "パスワードに空白を含めることはできません。";
    }
    if (value.length < 12) {
      return "パスワードは12文字以上である必要があります。";
    }
    if (value.length > 128) {
      return "パスワードは128文字以下である必要があります。";
    }
    return null;
  };
  const form = useForm({
    mode: "uncontrolled",
    initialValues: {
      newPassword: "",
      confirmNewPassword: "",
    },
    validate: {
      newPassword: passwordValidator,
      confirmNewPassword: matchesField(
        "newPassword",
        "新規パスワードが一致しません。"
      ),
    },
    validateInputOnChange: ["newPassword"],
    validateInputOnBlur: ["confirmNewPassword"],
  });

  const navigate = useNavigate();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const messagePasswordError = (message: string) => {
    setErrorMessage(message);
  };

  const fragment = useLocation().hash.substring(1);
  const queries = new URLSearchParams(fragment);
  const resetSessionId = queries.get("reset_session_id");

  const handleSetPassword = async (values: typeof form.values) => {
    if (values.newPassword !== values.confirmNewPassword) {
      messagePasswordError("新規パスワードが一致しません。");
      return;
    }
    if (!resetSessionId) {
      console.error("Reset session ID is not found.");
      return;
    }

    try {
      await resetPassword(resetSessionId, values.newPassword);
      navigate("/reset-password/success");
    } catch {
      navigate("/reset-password/failure");
    }
  };

  return (
    <Card
      title="パスワードリセット"
      description="新規パスワードを入力してください。"
    >
      <form onSubmit={form.onSubmit(handleSetPassword)}>
        <Stack className="gap-10">
          <div className="flex flex-col gap-5">
            <PasswordInput
              key={form.key("newPassword")}
              {...form.getInputProps("newPassword")}
              label="新規パスワード"
              placeholder="new password"
              required={true}
            />
            <PasswordInput
              key={form.key("confirmNewPassword")}
              {...form.getInputProps("confirmNewPassword")}
              label="新規パスワードの確認"
              placeholder="new password"
              required={true}
            />
          </div>
          {errorMessage && (
            <Text c="red" fw={700}>
              {errorMessage}
            </Text>
          )}
          <div className="flex justify-end">
            <Button text="登録" type="submit" />
          </div>
        </Stack>
      </form>
    </Card>
  );
}
